import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router'

import { useShippingLabelsStore } from '@/stores'
import { usePersonalPickupsStore } from '@/stores/shipments/personalPickups'
import { createPrefetchGuard } from '@/utils/routerGuards'
import Shipments from '@/views/Shipments/Index.vue'
import ShippingLabels from '@/views/Shipments/ShippingLabels/Index.vue'

const routes: RouteRecordRaw[] = [
    {
        path: '/shipments',
        name: 'shipmentsRoot',
        component: Shipments,
        meta: {
            title: 'shipments.pageTitle',
            menuRendered: true,
            menuTitle: 'shipments.menuTitle',
            requiresAuth: true,
            icon: 'mdi-package-variant-closed'
        },
        children: [
            {
                path: 'sendings',
                name: 'shipmentsSendings',
                component: () => import('@/views/Shipments/Sendings/Index.vue'),
                meta: {
                    title: 'shipments.sendings.pageTitle',
                    menuRendered: true,
                    menuTitle: 'shipments.sendings.menuTitle',
                    requiresAuth: true,
                    permission: 'sendings'
                }
            },
            {
                path: 'package-labels',
                name: 'shipmentsPackageLabels',
                component: ShippingLabels,
                redirect: {
                    name: 'shipmentsPackageLabelsLabels'
                },
                meta: {
                    title: 'shipments.shippingLabels.pageTitle',
                    menuRendered: true,
                    menuTitle: 'shipments.shippingLabels.menuTitle',
                    requiresAuth: true,
                    permission: 'labels'
                },
                children: [
                    {
                        path: 'labels',
                        name: 'shipmentsPackageLabelsLabels',
                        component: () => import('@/views/Shipments/ShippingLabels/Labels/Index.vue'),
                        meta: {
                            title: 'shipments.shippingLabels.listPageTitle',
                            requiresAuth: true,
                            permission: 'labels_read'
                        }
                    },
                    {
                        path: 'labels/create',
                        name: 'shipmentsPackageLabelsLabelsCreate',
                        component: () => import('@/views/Shipments/ShippingLabels/ShippingLabel.vue'),
                        meta: {
                            title: 'shipments.shippingLabels.createPageTitle',
                            requiresAuth: true,
                            permission: 'labels_create'
                        }
                    },
                    {
                        path: 'labels/edit/:id',
                        name: 'shipmentsPackageLabelsLabelsEdit',
                        component: () => import('@/views/Shipments/ShippingLabels/ShippingLabel.vue'),
                        beforeEnter: createPrefetchGuard({
                            getId: (to: RouteLocationNormalized) => to.params?.id,
                            fetch: async (id: string) => {
                                const shippingLabelsStore = useShippingLabelsStore()

                                if (String(shippingLabelsStore.shippingLabel?.id) === String(id)) {
                                    return
                                }

                                await shippingLabelsStore.fetchShippingLabel(id)
                            },
                            onMissingId: () => ({ name: 'shipmentsPackageLabelsLabels' }),
                            onError: () => ({ name: 'shipmentsPackageLabelsLabels' })
                        }),
                        meta: {
                            title: 'shipments.shippingLabels.editPageTitle',
                            requiresAuth: true,
                            permission: 'labels_update'
                        }
                    },
                    {
                        path: 'labels/edit-campaign/:id',
                        name: 'shipmentsPackageLabelsLabelsCampaignEdit',
                        component: () => import('@/views/Shipments/ShippingLabels/ShippingLabelCampaign.vue'),
                        beforeEnter: createPrefetchGuard({
                            getId: (to: RouteLocationNormalized) => to.params?.id,
                            fetch: async (id: string) => {
                                const shippingLabelsStore = useShippingLabelsStore()

                                if (String(shippingLabelsStore.shippingLabel?.id) === String(id)) {
                                    return
                                }

                                await shippingLabelsStore.fetchShippingLabel(id)
                            },
                            onMissingId: () => ({ name: 'shipmentsPackageLabelsLabels' }),
                            onError: () => ({ name: 'shipmentsPackageLabelsLabels' })
                        }),
                        meta: {
                            title: 'shipments.shippingLabels.campaignEditPageTitle',
                            requiresAuth: true,
                            permission: 'labels_update'
                        }
                    }
                ]
            },
            {
                path: 'sendings/reporting/:id',
                name: 'shipmentsSendingsReport',
                component: () => import('@/views/Shipments/Sendings/SendingsShipmentReportDetail.vue'),
                meta: {
                    title: 'shipments.sendings.reportDetail.pageTitle',
                    requiresAuth: true,
                    permission: 'shipments_read',
                    parentName: 'shipmentsSendings'
                }
            },
            {
                path: '/shipments/labels-generator',
                name: 'shipmentsLabelsGenerator',
                component: () => import('@/views/Shipments/LabelsGenerator/Index.vue'),
                meta: {
                    title: 'shipments.labelsGenerator.pageTitle',
                    menuRendered: true,
                    menuTitle: 'shipments.labelsGenerator.menuTitle',
                    requiresAuth: true,
                    permission: 'sendings'
                },
                redirect: { name: 'shipmentsLabelsGeneratorAll' },
                children: [
                    {
                        path: '',
                        name: 'shipmentsLabelsGeneratorAll',
                        component: () => import('@/views/Shipments/LabelsGenerator/LabelsGeneratorAll.vue'),
                        meta: {
                            title: 'shipments.labelsGenerator.all.pageTitle',
                            requiresAuth: true,
                            permission: 'sendings'
                        }
                    },
                    {
                        path: 'standard',
                        name: 'shipmentsLabelsGeneratorStandard',
                        component: () => import('@/views/Shipments/LabelsGenerator/StandardLabels/Index.vue'),
                        meta: {
                            title: 'shipments.labelsGenerator.standard.pageTitle',
                            requiresAuth: true,
                            permission: 'sendings'
                        }
                    },
                    {
                        path: 'billboard',
                        name: 'shipmentsLabelsGeneratorBillboard',
                        component: () => import('@/views/Shipments/LabelsGenerator/BillboardLabels/Index.vue'),
                        meta: {
                            title: 'shipments.labelsGenerator.billboard.pageTitle',
                            requiresAuth: true,
                            permission: 'sendings'
                        }
                    },
                    {
                        path: 'revision',
                        name: 'shipmentsLabelsGeneratorRevision',
                        component: () => import('@/views/Shipments/LabelsGenerator/RevisoryLabels/Index.vue'),
                        meta: {
                            title: 'shipments.labelsGenerator.revisory.pageTitle',
                            requiresAuth: true,
                            permission: 'sendings'
                        }
                    },
                    {
                        path: 'print-history',
                        name: 'shipmentsLabelsGeneratorPrintHistory',
                        component: () => import('@/views/Shipments/LabelsGenerator/LabelsPrintHistory/Index.vue'),
                        meta: {
                            title: 'shipments.labelsGenerator.history.pageTitle',
                            requiresAuth: true,
                            permission: 'sendings_admin_read'
                        }
                    }
                ]
            },
            {
                path: 'shipping-orders',
                name: 'shipmentsShipmentsOrders',
                component: () => import('@/views/Shipments/ShipmentsOrders/Index.vue'),
                meta: {
                    title: 'shippingOrders.pageTitle',
                    menuRendered: true,
                    menuTitle: 'shippingOrders.menuTitle',
                    requiresAuth: true,
                    permission: 'shipping_orders'
                }
            },
            {
                path: 'shipping-orders/:shippingOrderId',
                name: 'shipmentsShipmentsOrdersShipmentsOrder',
                component: () => import('@/views/Shipments/ShipmentsOrders/ShipmentsOrder/Index.vue'),
                meta: {
                    title: 'shippingOrders.details.pageTitle',
                    requiresAuth: true,
                    permission: 'shipping_orders',
                    parentName: 'shipmentsShipmentsOrders'
                }
            },
            {
                path: 'distributors',
                name: 'shipmentsDistributors',
                component: () => import('@/views/Shipments/Distributors/Index.vue'),
                meta: {
                    title: 'shipments.distributors.pageTitle',
                    menuRendered: true,
                    menuTitle: 'shipments.distributors.menuTitle',
                    requiresAuth: true,
                    permission: 'distributors'
                }
            },
            {
                path: 'distributors/:id',
                name: 'shipmentsDistributorsDetail',
                component: () => import('@/views/Shipments/Distributors/DistributorDetail.vue'),
                meta: {
                    title: 'shipments.distributors.detail.pageTitle',
                    requiresAuth: true,
                    permission: 'distributors'
                }
            },
            {
                path: 'personal-pickups',
                name: 'shipmentsPersonalPickups',
                component: () => import('@/views/Shipments/PersonalPickups/Index.vue'),
                meta: {
                    title: 'shipments.personalPickups.list.pageTitle',
                    menuRendered: true,
                    menuTitle: 'shipments.personalPickups.menuTitle',
                    requiresAuth: true,
                    permission: 'personal_pickups'
                }
            },
            {
                path: 'personal-pickups/create',
                name: 'shipmentsPersonalPickupsPersonalPickupCreate',
                component: () => import('@/views/Shipments/PersonalPickups/PersonalPickup.vue'),
                meta: {
                    title: 'shipments.personalPickups.details.pageTitleCreate',
                    requiresAuth: true,
                    permission: 'personal_pickups_create'
                }
            },
            {
                path: 'personal-packages-pickups/create',
                name: 'shipmentsPersonalPickupsPersonalPackagesPickupCreate',
                component: () => import('@/views/Shipments/PersonalPickups/PersonalPackagesPickup.vue'),
                meta: {
                    title: 'shipments.personalPickups.packageDetails.pageTitleCreate',
                    requiresAuth: true,
                    permission: 'personal_pickups_create'
                }
            },
            {
                path: 'personal-pickups/order/:id',
                name: 'shipmentsPersonalPickupsPersonalPickup',
                component: () => import('@/views/Shipments/PersonalPickups/PersonalPickup.vue'),
                beforeEnter: createPrefetchGuard({
                    getId: (to: RouteLocationNormalized) => to.params?.id,
                    fetch: async (id: string) => {
                        const personalPickupsStore = usePersonalPickupsStore()
                        await personalPickupsStore.prefetchPersonalPickup(Number(id))
                    },
                    onMissingId: () => ({ name: 'shipmentsPersonalPickups' }),
                    onError: () => ({ name: 'shipmentsPersonalPickups' })
                }),
                meta: {
                    title: 'shipments.personalPickups.details.pageTitleEdit',
                    requiresAuth: true,
                    permission: 'personal_pickups_read'
                }
            },
            {
                path: 'personal-pickups/package/:id',
                name: 'shipmentsPersonalPickupsPersonalPackagesPickup',
                component: () => import('@/views/Shipments/PersonalPickups/PersonalPackagesPickup.vue'),
                beforeEnter: createPrefetchGuard({
                    getId: (to: RouteLocationNormalized) => to.params?.id,
                    fetch: async (id: string) => {
                        const personalPickupsStore = usePersonalPickupsStore()
                        await personalPickupsStore.prefetchPersonalPickup(Number(id))
                    },
                    onMissingId: () => ({ name: 'shipmentsPersonalPickups' }),
                    onError: () => ({ name: 'shipmentsPersonalPickups' })
                }),
                meta: {
                    title: 'shipments.personalPickups.packageDetails.pageTitleEdit',
                    requiresAuth: true,
                    permission: 'personal_pickups_read'
                }
            }
        ]
    }
]

export default routes
